import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Ticket } from "lucide-react";
import { ReservaForm, RESERVA_FORM_VACIO, reservaToForm, formToPayload } from "@/components/reservas/ReservaForm";
import { ESTADOS_RESERVA, TRANSICIONES_RESERVA } from "@/lib/constants";

// PROMPT 142: lo que entra por Bókun se cambia o se cancela EN Bókun (misma regla que usa el
// servidor al leer los correos): si se reactiva acá y allá sigue cancelada, el próximo sync la cancela.
const vieneDeBokun = (r) =>
  (r?.sources || []).some((s) => String(s?.canal || "").toLowerCase() === "bokun") || !!r?.referencia_externa;

export default function ReservaFormDialog({ open, onOpenChange, tours, agencias = [], reserva, onSaved }) {
  const isEdit = !!reserva;
  const estadoActual = reserva?.estado || "pendiente"; // PROMPT 142: sin estado cargado el servidor asume "pendiente"
  const [form, setForm] = useState(RESERVA_FORM_VACIO);
  const [saving, setSaving] = useState(false);
  const [estado, setEstado] = useState("pendiente");
  const [reservaActual, setReservaActual] = useState(reserva);
  const [marcas, setMarcas] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(reserva ? reservaToForm(reserva) : RESERVA_FORM_VACIO);
      setEstado(reserva?.estado || "pendiente");
      setReservaActual(reserva);
      // PROMPT 86 (2026-09-01): cuenta/marca elegible SOLO al cargar a mano (no en
      // edicion: la edicion manda campos puntuales por PUT y no toca sources/marca).
      if (!reserva) {
        api.get("/marcas")
          .then(({ data }) => setMarcas((data || []).filter((m) => m.activo !== false)))
          .catch(() => setMarcas([]));
      } else {
        setMarcas([]);
      }
    }
  }, [open, reserva]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const validar = () => {
    if (!form.tour_id) return "Seleccioná un tour.";
    if (!form.fecha || !form.hora) return "Indicá fecha y hora.";
    if (!form.nombre.trim()) return "Indicá el nombre del pasajero.";
    if (!form.telefono || !form.telefono.trim()) return "Indicá el teléfono del pasajero (obligatorio).";
    if ((parseInt(form.adultos, 10) || 0) + (parseInt(form.ninos, 10) || 0) <= 0)
      return "Debe haber al menos un pasajero (adulto o niño).";
    return null;
  };

  const guardar = async () => {
    const err = validar();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (isEdit) {
        await api.put(`/reservas/${reserva.id}`, {
          pasajero: payload.pasajero,
          tour_id: payload.tour_id,
          fecha: payload.fecha,
          hora: payload.hora,
          modalidad: payload.modalidad,
          vehiculo: payload.vehiculo,
          tipo: payload.tipo,
          variante: payload.variante,
          adultos: payload.adultos,
          ninos: payload.ninos,
          infantes: payload.infantes,
          monto_pagado: payload.monto_pagado,
          saldo_pendiente: payload.saldo_pendiente,
          codigo_pago: payload.codigo_pago,
          monto_facturado_agencia: payload.monto_facturado_agencia,
          notas: payload.notas,
        });
        if ((form.agencia_id || "") !== (reserva.agencia_id || "")) {
          if (form.agencia_id) {
            await api.post(`/reservas/${reserva.id}/agencia`, { agencia_id: form.agencia_id });
          }
        }
        // PROMPT 142: el estado va al final. Si el servidor no lo cambia (ej. una cancelada que
        // no tiene lugar en su salida) lo demás YA se guardó: decirlo, porque antes el error
        // dejaba la duda de si se había guardado algo.
        const reactivando = reserva.estado === "cancelada" && estado !== "cancelada";
        if (estado !== reserva.estado) {
          try {
            await api.post(`/reservas/${reserva.id}/estado`, {
              nuevo_estado: estado,
              ...(reactivando ? { motivo: "Reactivada desde Editar reserva" } : {}),
            });
          } catch (e) {
            toast.error(`Se guardaron los demás cambios, pero el estado no cambió: ${formatApiError(e.response?.data?.detail)}`, { duration: 12000 });
            onSaved?.();
            return;
          }
        }
        if (reactivando) {
          toast.success(`Reserva ${reserva.codigo_booking} reactivada: vuelve a estar confirmada con el mismo código.`);
          if (vieneDeBokun(reserva)) {
            toast.warning("Esta reserva viene de Bókun: si allá sigue cancelada, la próxima sincronización la vuelve a cancelar. Revisalo en Bókun.", { duration: 12000 });
          }
        } else {
          toast.success("Reserva actualizada.");
        }
      } else {
        const { data } = await api.post("/reservas", payload);
        if (data?.duplicada) {
          // PROMPT 108: decir DONDE esta la que ya existe. "Ya existía" y nada mas dejaba al
          // equipo buscando una reserva que no aparecia en ningun lado (estaba en la Bandeja).
          const r = data.reserva || {};
          const donde = ["cancelada", "rechazada"].includes(r.estado)
            ? (r.estado === "cancelada"
              ? "está cancelada. Para volver a activarla con el mismo código, abrila en Reservas (lápiz) y cambiale el estado a Confirmada"
              : "está rechazada")
            : r.es_prueba
            ? "está marcada como reserva de prueba"
            : r.requiere_revision
            ? `está en la Bandeja, en "A revisar"${r.motivo_revision ? ` (${r.motivo_revision})` : ""}. Completala desde ahí`
            : "ya está en Reservas";
          const datos = [r.tour_nombre, r.fecha].filter(Boolean).join(", ");
          toast.warning(`La reserva ${r.codigo_booking || ""}${datos ? ` (${datos})` : ""} ya existe y ${donde}. No se creó otra.`, { duration: 12000 });
        } else {
          toast.success("Reserva creada.");
        }
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reserva-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Ticket className="h-5 w-5" /> {isEdit ? "Editar reserva" : "Cargar reserva manual"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Modificá los datos de la reserva. Los cambios quedan registrados en auditoría."
              : "Carga manual (venta directa, teléfono). Se mezcla con las reservas automáticas."}
          </DialogDescription>
        </DialogHeader>

        <ReservaForm
          form={form}
          set={set}
          tours={tours}
          agencias={agencias}
          marcas={marcas}
          showCodigo={!isEdit}
          reserva={reservaActual}
          onReservaUpdated={setReservaActual}
        />

        {isEdit && (
          <div className="border-t border-slate-100 pt-3 space-y-1.5">
            <Label className="text-xs">Estado de la reserva</Label>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="w-full sm:w-60" data-testid="reserva-estado-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* PROMPT 142: solo el estado actual y los que el servidor deja elegir desde ahí */}
                {Object.entries(ESTADOS_RESERVA)
                  .filter(([k]) => k === estadoActual || (TRANSICIONES_RESERVA[estadoActual] || []).includes(k))
                  .map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {estadoActual === "cancelada" && (
              <p className="text-xs text-slate-500" data-testid="reserva-reactivar-ayuda">
                Para volver a activar esta reserva con el mismo código, elegí Confirmada y guardá. Se revisa que la salida esté abierta y tenga lugar.
              </p>
            )}
            {estadoActual === "rechazada" && (
              <p className="text-xs text-slate-500" data-testid="reserva-rechazada-ayuda">
                Una reserva rechazada ya no cambia de estado.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reserva-form-cancel">
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={saving} data-testid="reserva-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Guardar cambios" : "Crear reserva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
